import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  trackInitialValues,
  TrackTabs,
} from 'components/organisms/forms/track/TrackForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiVinylRecord, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetTrack, usePutTrack } from 'services/tracks';
import {
  useDeleteTrackFile,
  usePostTrackChunkedFile,
  usePostTrackFile,
  usePutTrackChunkedFile,
  usePutTrackFile,
} from 'services/tracks/file';
import { ITrackFilePath, ITrackMutationForm } from 'services/tracks/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const EditTrack = () => {
  const trackId = useParamId('track');
  const { pathname } = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const getTrack = useGetTrack(trackId);

  const updateTrack = usePutTrack();

  const addTrackFile = usePostTrackFile();
  const addTrackChunkFile = usePostTrackChunkedFile();
  const updateTrackFile = usePutTrackFile(trackId);
  const updateTrackChunkFile = usePutTrackChunkedFile();
  const deleteTrackFile = useDeleteTrackFile(trackId);

  const mutateOptions = useMutateOptions<ITrackMutationForm>();

  const backPathname = `/track/${trackId}/${getLastPathname(pathname)}`;

  const onTrackFileMutationSuccess = async (
    formikHelpers: FormikHelpers<ITrackMutationForm>,
    value?: ITrackFilePath
  ) => {
    formikHelpers.setFieldValue('mp3Link', value?.mp3Link);
    formikHelpers.setFieldValue('mp3PreviewLink', value?.mp3PreviewLink);
    formikHelpers.setFieldValue('wavLink', value?.wavLink);
    formikHelpers.setFieldValue('file', undefined);
  };

  const areFileLinksExists = (
    wavLink?: string,
    mp3Link?: string,
    mp3PreviewLink?: string
  ) => wavLink?.length && mp3Link?.length && mp3PreviewLink?.length;

  const onEditTrack = async (
    values: ITrackMutationForm,
    formikHelpers: FormikHelpers<ITrackMutationForm>
  ) => {
    const initialValues =
      getTrack.data && middleware.track.queryToForm(getTrack.data);
    if (initialValues) {
      const { wavLink, mp3Link, mp3PreviewLink } = initialValues;

      if (
        values.file &&
        !areFileLinksExists(wavLink, mp3Link, mp3PreviewLink)
      ) {
        await addTrackChunkFile.mutateAsync(
          { file: values.file, trackId },
          {
            onError: error => onErrorShowToast(error, 'toast-message.add-file'),
            onSuccess: value =>
              onTrackFileMutationSuccess(formikHelpers, value),
          }
        );
      }

      if (values.file && areFileLinksExists(wavLink, mp3Link, mp3PreviewLink)) {
        await updateTrackChunkFile.mutateAsync(
          { file: values.file, trackId },
          {
            onError: error =>
              onErrorShowToast(error, 'toast-message.change-file'),
            onSuccess: value =>
              onTrackFileMutationSuccess(formikHelpers, value),
          }
        );
      }

      if (
        !values.file &&
        !areFileLinksExists(wavLink, mp3Link, mp3PreviewLink) &&
        areFileLinksExists(
          getTrack.data?.wavLink,
          getTrack.data?.mp3Link,
          getTrack.data?.mp3PreviewLink
        )
      ) {
        await deleteTrackFile.mutateAsync(undefined, {
          onError: error =>
            onErrorShowToast(error, 'toast-message.delete-file'),
          onSuccess: () => onTrackFileMutationSuccess(formikHelpers),
        });
      }
    }

    await updateTrack.mutateAsync(
      { ...middleware.track.formToQuery(values), trackId: trackId },
      {
        ...mutateOptions(formikHelpers),
        onSuccess: () => navigate(backPathname, { replace: true }),
      }
    );
  };

  return (
    <Form.Track
      i18nextPrefix='track'
      initialValues={
        getTrack.data
          ? middleware.track.queryToForm(getTrack.data)
          : trackInitialValues
      }
      disableTabs={[TrackTabs.statistics, TrackTabs.products]}
      backPath={backPathname}
      status={{ isLoading: getTrack.isLoading }}
      customBackItem={
        <Button
          type='button'
          onlyIcon
          onClick={() => {
            navigate(backPathname, { replace: true });
          }}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
      onSubmit={onEditTrack}
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.save')} />
          <Button.Icon icon={<PiVinylRecord />} />
        </Button>
      }
    />
  );
};

export default EditTrack;
